import AuthActionTypes from "./auth.types";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { navigate } from "gatsby";
import { toast } from "react-toastify";
import { AuthUser, LoginInput } from "@ifgengineering/client-auth-sdk";
import { authSdk } from "@utils/auth";
import { localStorageTutorialKey } from "../../layouts/layout";
import { logException } from "@helpers/logException/logException";
import { ResetPasswordInput } from "@ifgengineering/client-auth-sdk";
import { AppleCallbackInput } from "@ifgengineering/client-auth-sdk/src/types";
import { RootState } from "../rootReducer";
import { openMobileApp } from "@utils/openMobileApp";
import {
  forgetAnchoringDeal,
  forgetIntendedVisit,
  loginPathWithRedirect,
} from "@helpers/loginRedirect";
import { forgetAccreditationDraft } from "@components/OnboardingV2B/Accreditation/accreditationDraft";

export const signInAsync = createAsyncThunk(
  AuthActionTypes.SIGN_IN,
  async (
    { email, password, otp, token, captchaToken }: LoginInput,
    { rejectWithValue }
  ) => {
    try {
      const result = await authSdk.login({
        email,
        password,
        otp,
        token,
        captchaToken,
      });
      localStorage.removeItem("apple_id_token");
      return result.data;
    } catch (e: any) {
      if (e && e.response && e.response.data.statusCode === 429) {
        toast.error(e.response.data.message);
      }

      logException({
        scopeEmail: email,
        tag: "verifyOTP",
        exception: e,
      });

      return rejectWithValue(e);
    }
  }
);

const DONT_REPORT_HTTP_STATUSES = [401, 403, 409];

export const signUpAsync = createAsyncThunk<
  void,
  { email: string; password: string; captchaToken: string }
>(
  AuthActionTypes.SIGN_UP,
  async ({ email, password, captchaToken }, { rejectWithValue }) => {
    try {
      await authSdk.signupV2({ email, password, captchaToken });
      localStorage.setItem(localStorageTutorialKey, "false");
    } catch (e: any) {
      if (e && e.response && e.response.data.statusCode === 429) {
        toast.error(e.response.data.message);
      } else if (
        !DONT_REPORT_HTTP_STATUSES.some(
          (status) => status === e.response.data.statusCode
        )
      ) {
        logException({
          scopeEmail: email,
          tag: "signup",
          exception: e,
        });
      }

      return rejectWithValue(e);
    }
  }
);

interface SignOutOptions {
  returnToCurrentPage?: boolean;
}

export const signOutAsync = createAsyncThunk(
  AuthActionTypes.SIGN_OUT,
  async (options: SignOutOptions | undefined, { rejectWithValue }) => {
    try {
      await authSdk.logout();

      // An expired session returns where it left off; a logout starts fresh.
      if (options?.returnToCurrentPage) {
        navigate(loginPathWithRedirect());
        return;
      }

      forgetIntendedVisit();
      forgetAnchoringDeal();
      forgetAccreditationDraft();
      navigate("/app/login");
    } catch (e: any) {
      logException({
        tag: "logout",
        exception: e,
      });

      return rejectWithValue(JSON.stringify(e));
    }
  }
);

export const getUserInfoAsync = createAsyncThunk(
  AuthActionTypes.GET_AUTH_ACCOUNT_DATA,
  async (_, { rejectWithValue }) => {
    try {
      const response = await authSdk.userInfo();

      return response.user;
    } catch (e: any) {
      if (e.response.status !== 401) {
        logException({
          tag: "userInfo",
          exception: e,
        });
      }

      return rejectWithValue(e);
    }
  }
);

export const updateUserInfo = createAction<AuthUser>(
  AuthActionTypes.UPDATE_USER
);

export const resetAuth = createAction(AuthActionTypes.RESET);

export const resetPasswordAsync = createAsyncThunk(
  AuthActionTypes.RESET_PASSWORD,
  async (
    {
      email,
      newPassword,
      otp,
      token,
      isMobile,
    }: ResetPasswordInput & { isMobile?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await authSdk.resetPassword({
        email,
        newPassword,
        otp,
        token,
      });
      toast.success("You successfully changed your password!");

      if (isMobile) {
        setTimeout(() => {
          openMobileApp();
        }, 2000);
      } else {
        navigate("/app/login");
      }
      return response;
    } catch (e: any) {
      if (e.response.status === 401) {
        toast.error(
          "Your reset password link has expired. Please regenerate a link via the forgot password page."
        );
      } else {
        logException({
          scopeEmail: email,
          tag: "userInfo",
          exception: e,
        });

        toast.error("Something went wrong! Try again later.");
      }
      return rejectWithValue(e);
    }
  }
);

export const updateTwoFactorAuthAsync = createAsyncThunk<any, any>(
  AuthActionTypes.UPDATE_TWO_FACTOR_AUTH,
  async (
    { isTwoFactorAuthEnabled, primaryTwoFactorAuthMethod, otp },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await authSdk.updateTwoFactorAuthSettings({
        isTwoFactorAuthEnabled,
        primaryTwoFactorAuthMethod,
        otp,
      });
      toast.success("Security details updated successfully!");
      dispatch(getUserInfoAsync());
      return response;
    } catch (e) {
      logException({
        tag: "updateTwoFactorAuthSettings",
        exception: e,
      });

      toast.error("Something went wrong! Try again later.");
      return rejectWithValue(e);
    }
  }
);

export const signInGoogleAsync = createAsyncThunk<any, any>(
  AuthActionTypes.SIGN_IN_GOOGLE,
  async ({ authorizationCode, isSignup = false }, { rejectWithValue }) => {
    try {
      await authSdk.googleLogin({
        authorizationCode,
      });

      localStorage.removeItem("apple_id_token");

      // A login's destination is the login page's call: it may owe a deep link.
      if (isSignup) {
        navigate("/app/onboarding");
      }
    } catch (e) {
      logException({
        tag: "signInGoogle",
        exception: e,
      });
      toast.error("Something went wrong! Try again later.");
      return rejectWithValue(e);
    }
  }
);

export const signInAppleAsync = createAsyncThunk<
  any,
  AppleCallbackInput & { isSignup?: boolean }
>(
  AuthActionTypes.SIGN_IN_APPLE,
  async (
    { authorizationCode, appleIdToken, isSignup = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await authSdk.appleCallback({
        authorizationCode,
        appleIdToken,
      });

      const isPrivateRelayEmail = response.data?.isPrivateRelayEmail;
      const email = response.data?.email;

      if (isPrivateRelayEmail) {
        localStorage.removeItem("apple_id_token");
        navigate("/app/signup-apple");
        return { isPrivateRelayEmail };
      }

      if (email) {
        localStorage.removeItem("apple_id_token");

        // A login's destination is the login page's call, as in Google above.
        if (isSignup) {
          navigate("/app/onboarding");
        }

        return { email };
      }
    } catch (e) {
      logException({
        tag: "signInApple",
        exception: e,
      });
      toast.error("Something went wrong! Try again later.");
      return rejectWithValue(e);
    }
  }
);

export const signupAppleAsync = createAsyncThunk<any, any>(
  AuthActionTypes.SIGN_UP_APPLE,
  async ({ authorizationCode }, { rejectWithValue }) => {
    try {
      const response = await authSdk.appleSignup({
        authorizationCode,
      });

      if (response.data?.email) {
        localStorage.removeItem("apple_id_token");
        navigate("/app/onboarding");
      }
    } catch (e) {
      logException({
        tag: "signupApple",
        exception: e,
      });
      toast.error("Something went wrong! Try again later.");
      return rejectWithValue(e);
    }
  }
);

export const resetPassword = async ({
  email,
  newPassword,
  token,
}: {
  email: string;
  newPassword: string;
  token: string;
}): Promise<void> => {
  return await authSdk.resetPassword({ email, newPassword, token });
};

export const authErrorAsync = createAsyncThunk<
  void,
  {
    err: Error;
  }
>(AuthActionTypes.AUTH_ERROR, () => {
  toast.info("Please log in");
});
